import React, { useContext } from 'react'
import copy from 'copy-to-clipboard'
import { useRouteMatch } from 'react-router-dom'
import { useHistory } from 'react-router'
import { usePrototypeState } from '@sketch-hq/sketch-web-renderer'

import {
  ContextMenuPassedProps,
  Dropdown,
  ModalContext,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import {
  isPreviewRoute,
  LaunchSketchAlert,
  useFlag,
} from '@sketch/modules-common'
import { PartialShareModalCreate } from '../../../shares/modals/PartialShareModal'
import { IS_EMBEDDED } from '@sketch/constants'

import { useVersioning } from '../../../versioning'
import { INSPECTOR_SEGMENT } from '../../../shares/constants'
import {
  useCreateDraftAnnotation,
  useIsAnnotationContextAvailable,
} from '../../../annotations/hooks'

interface StatePassedContextMenu {
  frameGroupUUID: string | null
  pageUUID: string | null
}

interface PrototypeContextMenuProps
  extends ContextMenuPassedProps<StatePassedContextMenu> {
  initialFrameGroupUUID: string
}
/**
 * Menu displayed when right clicking on the prototype.
 */
export function PrototypeContextMenu(props: PrototypeContextMenuProps) {
  const {
    initialFrameGroupUUID,
    positionRelativeToContainer,
    additionalState,
  } = props

  const { showToast } = useToast()
  const { showModal } = useContext(ModalContext)
  const { path } = useRouteMatch()
  const isPreviewView = isPreviewRoute(path)
  const history = useHistory()
  const isPartialSharingEnabled = useFlag('partial-sharing')

  const { currentArtboardId } = usePrototypeState()
  const { getPathname, share, latestVersionId } = useVersioning()

  const isAnnotationsAvailable = useIsAnnotationContextAvailable()
  const handleDraftAnnotationEvent = useCreateDraftAnnotation(share.identifier)
  const frameGroupUUID = additionalState.frameGroupUUID || initialFrameGroupUUID

  function handleCopyLinkToArtboard() {
    const currentPathname = getPathname({
      routeKey: 'PROTOTYPE_PLAYER',
      routeParams: {
        shareID: share.identifier,
        prototypeArtboardUUID: currentArtboardId,
        currentArtboardUUID: currentArtboardId,
      },
    })

    const link = `${window.location.origin}${currentPathname}`
    copy(link)
    showToast('Link copied')
  }

  function handleEdit() {
    const pageUUID = additionalState.pageUUID || undefined

    showModal(LaunchSketchAlert, {
      shareId: share.identifier,
      pageUUID,
      action: 'openInSketch',
    })
  }

  function handleInspectArtboard() {
    const currentPathname = getPathname({
      routeKey: 'FRAME',
      routeParams: {
        shareID: share.identifier,
        frameUUID: frameGroupUUID,
      },
    })

    history.push(`${currentPathname}#${INSPECTOR_SEGMENT}`)
  }

  const handleAddAnnotation = () => {
    handleDraftAnnotationEvent(
      {
        clientX: positionRelativeToContainer.x,
        clientY: positionRelativeToContainer.y,
      },
      true
    )
  }

  const hasFrameGroupUUID = frameGroupUUID !== null

  const showInspectArtboard =
    share.userCanInspect && hasFrameGroupUUID && !IS_EMBEDDED
  const showCreatePreview =
    isPartialSharingEnabled && share.userCanUpdateSettings

  return (
    <>
      <Dropdown.Header>PROTOTYPE</Dropdown.Header>

      {frameGroupUUID !== null && !isPreviewView && (
        <Dropdown.Item onClick={handleCopyLinkToArtboard}>
          Copy Link
        </Dropdown.Item>
      )}

      {showInspectArtboard && !isPreviewView && (
        <Dropdown.Item onClick={handleInspectArtboard}>
          View Frame
        </Dropdown.Item>
      )}

      {(share.commentsEnabled || share.userCanEdit) && <Dropdown.Divider />}

      {additionalState.frameGroupUUID &&
        isAnnotationsAvailable &&
        share.commentsEnabled && (
          <Dropdown.Item onClick={handleAddAnnotation}>
            Add Comment…
          </Dropdown.Item>
        )}

      {share.userCanEdit && (
        <Dropdown.Item onClick={handleEdit}>Open in Mac App…</Dropdown.Item>
      )}

      {showCreatePreview && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() => {
              showModal(PartialShareModalCreate, {
                shareIdentifier: share.identifier,
                shareName: share.name,
                versionShortId: latestVersionId,
              })
            }}
          >
            Share a Preview of this Page…
          </Dropdown.Item>
        </>
      )}
    </>
  )
}
